/**
 * /accept-invitation?token=… — публічна сторінка прийняття запрошення.
 *
 * Сценарії:
 *   D. Токен недійсний/застарів/запрошення скасовано → пояснення.
 *   A. Обліковий запис уже є, користувач не увійшов → «увійдіть під X»
 *      (+ «забули пароль?»); після входу повертаємось сюди (return_to).
 *   B. Увійшов під тим самим email → кнопка «прийняти запрошення».
 *      ч.1073 (зауваження власника): пароль існуючому користувачу НЕ
 *      пропонуємо — він уже зареєстрований. Раніше сторінка визначала
 *      «хто увійшов» через /auth/me, який вимагає обраної організації:
 *      щойно увійшовши (організацію ще не обрано), користувач бачив форму
 *      «створіть пароль». Тепер — /auth/account-security (без організації).
 *   Edge. Увійшов під іншим email → «увійдіть під X».
 *   C. Нового облікового запису ще немає → ПІБ + пароль → прийняти →
 *      вхід під новим записом.
 *
 * Бекенд перевіряє те саме незалежно від сторінки: існуючий обліковий запис
 * приймає запрошення лише увійшовши під ним (інакше 403).
 */
import { useState, type FormEvent } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery } from '@tanstack/react-query';
import {
  acceptInvitation,
  getInvitationInfo,
  type InvitationInfo,
} from '@/api/invitations';
import { account, login as apiLogin } from '@/api/auth';
import { isLoggedIn, enterTenant, setSession } from '@/auth/store';
import LegalConsent from '@/shared/components/LegalConsent';
import { extractApiError } from '@/utils/extractApiError';

const box = { width: 'min(520px, 100%)', padding: '2rem', background: 'var(--bg-panel)' };

export default function AcceptInvitationPage() {
  const [params] = useSearchParams();
  const token = params.get('token');
  const hasToken = Boolean(token);

  const info = useQuery<InvitationInfo>({
    queryKey: ['invitation-info', token],
    queryFn: () => getInvitationInfo(token!),
    enabled: hasToken,
    retry: false,
  });

  // Хто увійшов — без прив'язки до організації (на відміну від /auth/me).
  const loggedIn = isLoggedIn();
  const me = useQuery({
    queryKey: ['account-security'],
    queryFn: account.security,
    enabled: loggedIn,
    retry: false,
  });

  if (!hasToken || info.isError) return <InvalidInvitation />;
  if (info.isLoading || !info.data || (loggedIn && me.isLoading)) {
    return (
      <div className="login">
        <div className="card" style={box}>Завантаження…</div>
      </div>
    );
  }

  const inv = info.data;
  const myEmail = loggedIn && me.data ? me.data.email : null;
  const loginLink = `/login?return_to=${encodeURIComponent(
    `/accept-invitation?token=${token}`)}`;

  if (!inv.user_exists) {
    return <NewAccountForm token={token!} info={inv} loggedInAs={myEmail} />;
  }

  if (myEmail !== null && myEmail.toLowerCase() === inv.invited_email.toLowerCase()) {
    return <AcceptExisting token={token!} info={inv} />;
  }

  return (
    <div className="login">
      <div className="card" style={box}>
        <h2 style={{ marginTop: 0 }}>
          {myEmail ? 'Це запрошення — для іншого користувача' : `Запрошення до «${inv.tenant_name}»`}
        </h2>
        <div style={{ marginBottom: '1rem', color: 'var(--text-secondary)' }}>
          <div>Запрошення для: <strong>{inv.invited_email}</strong></div>
          {myEmail ? (
            <div>Ви увійшли як: <strong>{myEmail}</strong></div>
          ) : (
            <div>Роль: <strong>{inv.role_display}</strong></div>
          )}
        </div>
        <p style={{ color: 'var(--text-secondary)' }}>
          Обліковий запис <strong>{inv.invited_email}</strong> уже зареєстровано —
          новий пароль не потрібен. Увійдіть під ним, щоб прийняти запрошення.
        </p>
        <div style={{ marginTop: '1.5rem', display: 'flex', gap: '1rem', alignItems: 'center', flexWrap: 'wrap' }}>
          <Link to={loginLink}>
            <button type="button" className="primary">
              {myEmail ? 'увійти іншим обліковим записом' : 'увійти і прийняти'}
            </button>
          </Link>
          <Link to={`/forgot-password?email=${encodeURIComponent(inv.invited_email)}`}>
            Забули пароль?
          </Link>
        </div>
      </div>
    </div>
  );
}

function InvalidInvitation() {
  return (
    <div className="login">
      <div className="card" style={box}>
        <h2 style={{ marginTop: 0 }}>Запрошення недійсне</h2>
        <p style={{ color: 'var(--text-secondary)' }}>Можливі причини:</p>
        <ul style={{ color: 'var(--text-secondary)', paddingLeft: '1.2rem' }}>
          <li>Термін запрошення минув (діє 7 днів)</li>
          <li>Запрошення скасовано або надіслано нове</li>
          <li>Посилання пошкоджене або неповне</li>
        </ul>
        <p style={{ color: 'var(--text-secondary)' }}>
          Зверніться до того, хто вас запросив, щоб отримати нове.
        </p>
        <div style={{ marginTop: '1.5rem' }}>
          <Link to="/login">
            <button type="button">на головну</button>
          </Link>
        </div>
      </div>
    </div>
  );
}

/** Після прийняття: робимо акаунт активним і ведемо на головну. */
function useFinish(info: InvitationInfo) {
  // ч.1229: вхід у клієнта — лише з повним перезавантаженням (enterTenant): сховища і кеш
  // запитів сторінки могли бути від іншого клієнта, у якому користувач працював до цього.
  return () => enterTenant(info.tenant_id, '/');
}

/** B: увійшов під запрошеним email — лише підтвердити. */
function AcceptExisting({ token, info }: { token: string; info: InvitationInfo }) {
  const finish = useFinish(info);
  const [error, setError] = useState<string | null>(null);
  const accept = useMutation({
    mutationFn: () => acceptInvitation({ token }),
    onSuccess: finish,
    onError: (e) => setError(extractApiError(e, 'Не вдалося прийняти запрошення')),
  });
  return (
    <div className="login">
      <div className="card" style={box}>
        <h2 style={{ marginTop: 0 }}>Запрошення до «{info.tenant_name}»</h2>
        <div style={{ marginBottom: '1rem', color: 'var(--text-secondary)' }}>
          <div>Email: <strong>{info.invited_email}</strong></div>
          <div>Роль: <strong>{info.role_display}</strong></div>
        </div>
        <button
          type="button"
          className="primary"
          style={{ width: '100%' }}
          disabled={accept.isPending}
          onClick={() => { setError(null); accept.mutate(); }}
        >
          {accept.isPending ? 'прийняття…' : 'прийняти запрошення'}
        </button>
        {error && <div className="error">{error}</div>}
      </div>
    </div>
  );
}

/** C: нового облікового запису ще немає — ПІБ і пароль. */
function NewAccountForm({ token, info, loggedInAs }: {
  token: string;
  info: InvitationInfo;
  /** Уже увійшов під ІНШИМ записом — після створення нового увійдемо під ним. */
  loggedInAs: string | null;
}) {
  const finish = useFinish(info);
  const [fullName, setFullName] = useState('');
  const [password, setPassword] = useState('');
  const [passwordConfirm, setPasswordConfirm] = useState('');
  const [accepted, setAccepted] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const accept = useMutation({
    mutationFn: async () => {
      await acceptInvitation({ token, password, full_name: fullName.trim(), accept_terms: true });
      // Новий обліковий запис (без 2FA) — вхід одразу віддає токени.
      const res = await apiLogin(info.invited_email, password);
      if (!res.requires_2fa) {
        setSession(res.user_id ?? null);
      }
    },
    onSuccess: finish,
    onError: (e) => setError(extractApiError(e, 'Не вдалося прийняти запрошення')),
  });

  function onSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    if (!fullName.trim()) {
      setError('Вкажіть ваше імʼя та прізвище');
      return;
    }
    if (password.length < 8) {
      setError('Пароль має містити мінімум 8 символів');
      return;
    }
    if (password !== passwordConfirm) {
      setError('Паролі не співпадають');
      return;
    }
    accept.mutate();
  }

  return (
    <div className="login">
      <form onSubmit={onSubmit} style={box}>
        <h2 style={{ marginTop: 0 }}>Запрошення до «{info.tenant_name}»</h2>
        <div style={{ marginBottom: '1rem', color: 'var(--text-secondary)' }}>
          <div>Email: <strong>{info.invited_email}</strong></div>
          <div>Роль: <strong>{info.role_display}</strong></div>
        </div>
        {loggedInAs && (
          <p style={{ color: 'var(--badge-amber-fg)', fontSize: '0.875rem' }}>
            Зараз ви увійшли як {loggedInAs}. Після створення облікового запису{' '}
            {info.invited_email} ви увійдете під ним.
          </p>
        )}
        <p style={{ color: 'var(--text-secondary)', fontSize: '0.875rem' }}>
          Створіть обліковий запис: вкажіть імʼя та пароль для входу.
        </p>
        <div className="field">
          <label htmlFor="inv-full-name">Прізвище та імʼя</label>
          <input
            id="inv-full-name"
            value={fullName}
            onChange={(e) => setFullName(e.target.value)}
            autoComplete="name"
            maxLength={255}
            required
            autoFocus
          />
        </div>
        <div className="field">
          <label htmlFor="inv-password">Пароль (мін. 8 символів)</label>
          <input
            id="inv-password"
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            autoComplete="new-password"
            required
            minLength={8}
          />
        </div>
        <div className="field">
          <label htmlFor="inv-password2">Повторіть пароль</label>
          <input
            id="inv-password2"
            type="password"
            value={passwordConfirm}
            onChange={(e) => setPasswordConfirm(e.target.value)}
            autoComplete="new-password"
            required
          />
        </div>
        <LegalConsent checked={accepted} onChange={setAccepted} />
        <button
          type="submit"
          className="primary"
          style={{ width: '100%' }}
          disabled={accept.isPending}
        >
          {accept.isPending ? 'створення…' : 'створити обліковий запис і приєднатися'}
        </button>
        {error && <div className="error">{error}</div>}
      </form>
    </div>
  );
}
